import {FilterEnum} from 'Agent/Commissions/ducks/commission';
import {DateRange} from 'Core/models/DateRange';
import {FilterOptionUnion} from 'Core/models/FilterOption';
import {InequalityRangeFilter} from 'Core/models/InequalityRange';
import {
  withDateField,
  withDateRangeField,
  withInequalityField,
  withOptionField,
  withTextField,
  withRegExTextField,
} from 'Shared/components/WithTextField';
import {GetPropertiesOfType} from 'Shared/utilities/GetPropertiesOfType';

export enum FilterType {
  Text = 1,
  DateRange = 2,
  Select = 3,
  Date = 4,
  Inequality = 5,
  RegEx = 6,
}

type FilterPropertiesBase<TFilter> = {
  changeFilter: ChangeFilterFunction<TFilter>;
  filterData: TFilter;
};

type SingleSelectFilterProperties<TFilter> = {
  filterKey: keyof GetPropertiesOfType<TFilter, FilterOptionUnion>;
  multiple?: false;
};

type MultiSelectFilterProperties<TFilter> = {
  filterKey: keyof GetPropertiesOfType<TFilter, FilterOptionUnion[]>;
  multiple: true;
};

export type SelectFilterProperties<TFilter> = (
  | SingleSelectFilterProperties<TFilter>
  | MultiSelectFilterProperties<TFilter>
) & {
  filterType: FilterType.Select;
  items: FilterEnum[];
} & FilterPropertiesBase<TFilter>;

type StringFilterProperties<TFilter> = {
  filterKey: keyof GetPropertiesOfType<TFilter, string | number>;
  filterType: FilterType.Text;
} & FilterPropertiesBase<TFilter>;

type RegExStringFilterProperties<TFilter> = {
  filterKey: keyof GetPropertiesOfType<TFilter, string>;
  filterType: FilterType.RegEx;
  pattern?: RegExp;
} & FilterPropertiesBase<TFilter>;

type DateRangeFilterProperties<TFilter> = {
  filterKey: keyof GetPropertiesOfType<TFilter, DateRange>;
  filterType: FilterType.DateRange;
} & FilterPropertiesBase<TFilter>;

type DateFilterProperties<TFilter> = {
  filterKey: keyof GetPropertiesOfType<TFilter, string>;
  filterType: FilterType.Date;
} & FilterPropertiesBase<TFilter>;

type InequalityFilterProperties<TFilter> = {
  filterKey: keyof GetPropertiesOfType<TFilter, InequalityRangeFilter>;
  filterType: FilterType.Inequality;
} & FilterPropertiesBase<TFilter>;

export type FilterProperties<TFilter> =
  | StringFilterProperties<TFilter>
  | DateRangeFilterProperties<TFilter>
  | InequalityFilterProperties<TFilter>
  | SelectFilterProperties<TFilter>
  | DateFilterProperties<TFilter>
  | RegExStringFilterProperties<TFilter>;

export type ChangeFilterFunction<TFilter> = (fieldPropertyName: keyof TFilter) => (value: string) => void;

export const withSelectFilterProperties = <TFilter>(
  baseProperties: FilterPropertiesBase<TFilter>,
  key: keyof GetPropertiesOfType<TFilter, FilterOptionUnion | FilterOptionUnion[]>,
  items: FilterEnum[],
  multiple?: boolean
): SelectFilterProperties<TFilter> => {
  let props;

  if (multiple) {
    props = {
      filterKey: key as unknown,
      multiple: true,
    } as MultiSelectFilterProperties<TFilter>;
  } else {
    props = {
      filterKey: key as unknown,
      multiple: false,
    } as SingleSelectFilterProperties<TFilter>;
  }

  return {
    ...baseProperties,
    filterType: FilterType.Select,
    items,
    ...props,
  };
};

export const withStringFilterProperties = <TFilter>(
  baseProperties: FilterPropertiesBase<TFilter>,
  key: keyof GetPropertiesOfType<TFilter, string | number>
): StringFilterProperties<TFilter> => ({
  ...baseProperties,
  filterKey: key,
  filterType: FilterType.Text,
});

export const withRegExStringFilterProperties = <TFilter>(
  baseProperties: FilterPropertiesBase<TFilter>,
  key: keyof GetPropertiesOfType<TFilter, string>,
  pattern?: RegExp
): RegExStringFilterProperties<TFilter> => ({
  ...baseProperties,
  filterKey: key,
  filterType: FilterType.RegEx,
  pattern: pattern,
});

export const withDateFilterProperties = <TFilter>(
  baseProperties: FilterPropertiesBase<TFilter>,
  key: keyof GetPropertiesOfType<TFilter, DateRange>
): DateRangeFilterProperties<TFilter> => ({
  ...baseProperties,
  filterKey: key,
  filterType: FilterType.DateRange,
});

export const withInequalityFilterProperties = <TFilter>(
  baseProperties: FilterPropertiesBase<TFilter>,
  key: keyof GetPropertiesOfType<TFilter, InequalityRangeFilter>
): InequalityFilterProperties<TFilter> => ({
  ...baseProperties,
  filterKey: key,
  filterType: FilterType.Inequality,
});

export const withSingleDateFilterProperties = <TFilter>(
  baseProperties: FilterPropertiesBase<TFilter>,
  key: keyof GetPropertiesOfType<TFilter, string>
): DateFilterProperties<TFilter> => ({
  ...baseProperties,
  filterKey: key,
  filterType: FilterType.Date,
});

const getFilterComponent = <TFilter>(label: string, filterProperties: FilterProperties<TFilter>) => {
  if (filterProperties.filterType === FilterType.Text) {
    return withTextField<TFilter, typeof filterProperties.filterKey>(
      label,
      filterProperties.filterKey,
      filterProperties.changeFilter
    )(filterProperties.filterData);
  } else if (filterProperties.filterType === FilterType.RegEx) {
    return withRegExTextField<TFilter, typeof filterProperties.filterKey>(
      label,
      filterProperties.filterKey,
      filterProperties.changeFilter,
      filterProperties.pattern
    )(filterProperties.filterData);
  } else if (filterProperties.filterType === FilterType.DateRange) {
    return withDateRangeField<TFilter, keyof TFilter>(
      label,
      filterProperties.filterKey,
      filterProperties.changeFilter
    )(filterProperties.filterData);
  } else if (filterProperties.filterType === FilterType.Date) {
    return withDateField<TFilter, keyof TFilter>(
      label,
      filterProperties.filterKey,
      filterProperties.changeFilter
    )(filterProperties.filterData);
  } else if (filterProperties.filterType === FilterType.Select) {
    return withOptionField<TFilter, keyof TFilter>(
      label,
      filterProperties.filterKey,
      filterProperties.changeFilter,
      filterProperties.items,
      !!filterProperties.multiple
    )(filterProperties.filterData);
  } else if (filterProperties.filterType === FilterType.Inequality) {
    return withInequalityField<TFilter, keyof TFilter>(
      label,
      filterProperties.filterKey,
      filterProperties.changeFilter
    )(filterProperties.filterData);
  }
};

export default getFilterComponent;
